import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import {
    Plus,
    Pencil,
    Trash2,
    Loader2,
    Sparkles,
    Zap,
    MessageSquare,
    Bot,
    Type,
    Layout,
    Palette,
    Save,
} from "lucide-react";
import { toast } from "sonner";

interface PromptAiTemplate {
    id: string;
    title: string;
    title_th: string | null;
    description: string | null;
    description_th: string | null;
    prompt: string;
    credits_required: number;
    color_class: string;
    icon_name: string;
    sort_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

const ICON_OPTIONS = [
    { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
    { value: 'bot', label: 'Bot', icon: Bot },
    { value: 'message', label: 'Message', icon: MessageSquare },
    { value: 'zap', label: 'Zap', icon: Zap },
    { value: 'type', label: 'Type', icon: Type },
    { value: 'layout', label: 'Layout', icon: Layout },
];

const COLOR_OPTIONS = [
    { value: 'from-blue-500/20 to-purple-500/20', label: 'Blue to Purple', preview: 'bg-gradient-to-r from-blue-500 to-purple-500' },
    { value: 'from-amber-500/20 to-orange-500/20', label: 'Amber to Orange', preview: 'bg-gradient-to-r from-amber-500 to-orange-500' },
    { value: 'from-pink-500/20 to-rose-500/20', label: 'Pink to Rose', preview: 'bg-gradient-to-r from-pink-500 to-rose-500' },
    { value: 'from-red-500/20 to-orange-500/20', label: 'Red to Orange', preview: 'bg-gradient-to-r from-red-500 to-orange-500' },
    { value: 'from-green-500/20 to-teal-500/20', label: 'Green to Teal', preview: 'bg-gradient-to-r from-green-500 to-teal-500' },
    { value: 'from-violet-500/20 to-indigo-500/20', label: 'Violet to Indigo', preview: 'bg-gradient-to-r from-violet-500 to-indigo-500' },
];

const PromptAiManagement = () => {
    const [templates, setTemplates] = useState<PromptAiTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form state
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        title: "",
        title_th: "",
        description: "",
        description_th: "",
        prompt: "",
        credits_required: 4,
        color_class: "from-blue-500/20 to-purple-500/20",
        icon_name: "sparkles",
        sort_order: 0,
        is_active: true,
    });

    // Dialog states
    const [showForm, setShowForm] = useState(false);

    // Fetch templates
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from("prompt_ai_templates" as any)
                .select("*")
                .order("sort_order", { ascending: true });

            if (error) throw error;
            setTemplates((data || []) as any);
        } catch (error) {
            console.error("Error fetching templates:", error);
            toast.error("Failed to load templates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Reset form
    const resetForm = () => {
        setFormData({
            title: "",
            title_th: "",
            description: "",
            description_th: "",
            prompt: "",
            credits_required: 4,
            color_class: "from-blue-500/20 to-purple-500/20",
            icon_name: "sparkles",
            sort_order: templates.length,
            is_active: true,
        });
        setEditingId(null);
    };

    // Open form for new template
    const handleNew = () => {
        resetForm();
        setShowForm(true);
    };

    // Open form for editing
    const handleEdit = (template: PromptAiTemplate) => {
        setFormData({
            title: template.title,
            title_th: template.title_th || "",
            description: template.description || "",
            description_th: template.description_th || "",
            prompt: template.prompt,
            credits_required: template.credits_required,
            color_class: template.color_class,
            icon_name: template.icon_name,
            sort_order: template.sort_order,
            is_active: template.is_active,
        });
        setEditingId(template.id);
        setShowForm(true);
    };

    // Save template
    const handleSave = async () => {
        if (!formData.title.trim()) {
            toast.error("Title is required");
            return;
        }
        if (!formData.prompt.trim()) {
            toast.error("Prompt is required");
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                // Update existing
                const { error } = await supabase
                    .from("prompt_ai_templates" as any)
                    .update(formData as any)
                    .eq("id", editingId);

                if (error) throw error;
                toast.success("Template updated successfully");
            } else {
                // Create new
                const { error } = await supabase
                    .from("prompt_ai_templates" as any)
                    .insert(formData as any);

                if (error) throw error;
                toast.success("Template created successfully");
            }

            setShowForm(false);
            resetForm();
            fetchTemplates();
        } catch (error) {
            console.error("Error saving template:", error);
            toast.error("Failed to save template");
        } finally {
            setSaving(false);
        }
    };

    // Delete template
    const handleDelete = async (id: string) => {
        try {
            const { error } = await supabase
                .from("prompt_ai_templates" as any)
                .delete()
                .eq("id", id);

            if (error) throw error;
            toast.success("Template deleted successfully");
            fetchTemplates();
        } catch (error) {
            console.error("Error deleting template:", error);
            toast.error("Failed to delete template");
        }
    };

    // Toggle active status
    const handleToggleActive = async (id: string, isActive: boolean) => {
        try {
            const { error } = await supabase
                .from("prompt_ai_templates" as any)
                .update({ is_active: !isActive } as any)
                .eq("id", id);

            if (error) throw error;
            toast.success("Template status updated");
            fetchTemplates();
        } catch (error) {
            console.error("Error updating template:", error);
            toast.error("Failed to update template");
        }
    };

    const getIconComponent = (iconName: string) => {
        const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
        if (!iconOption) return Sparkles;
        return iconOption.icon;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Bot className="h-6 w-6 text-primary" />
                        Prompt AI Templates
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage specialized gemini prompts and chat templates for users
                    </p>
                </div>
                <Button onClick={handleNew} className="gap-2">
                    <Plus className="h-4 w-4" />
                    New Template
                </Button>
            </div>

            {/* Templates List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => {
                    const IconComponent = getIconComponent(template.icon_name);
                    return (
                        <Card
                            key={template.id}
                            className={`overflow-hidden transition-all hover:border-primary/50 ${!template.is_active ? "opacity-60" : ""}`}
                        >
                            <div
                                className={`w-full aspect-video bg-gradient-to-br ${template.color_class} flex items-center justify-center p-6 relative`}
                            >
                                <IconComponent className="h-16 w-16 text-white/50" />
                                {!template.is_active && (
                                    <Badge className="absolute top-2 right-2" variant="secondary">
                                        Inactive
                                    </Badge>
                                )}
                                <Badge variant="outline" className="bg-black/20 text-white border-white/20 absolute top-2 left-2">
                                    {template.credits_required} Credits
                                </Badge>
                            </div>

                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="text-lg">{template.title}</CardTitle>
                                        {template.title_th && (
                                            <p className="text-sm text-muted-foreground mt-1">{template.title_th}</p>
                                        )}
                                    </div>
                                    <Switch
                                        checked={template.is_active}
                                        onCheckedChange={() => handleToggleActive(template.id, template.is_active)}
                                    />
                                </div>
                            </CardHeader>

                            <CardContent className="space-y-4">
                                {template.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
                                        {template.description}
                                    </p>
                                )}

                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1"
                                        onClick={() => handleEdit(template)}
                                    >
                                        <Pencil className="h-3 w-3 mr-1" />
                                        Edit
                                    </Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10">
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Template?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    This action cannot be undone. This will permanently delete the template.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => handleDelete(template.id)}
                                                    className="bg-destructive text-destructive-foreground"
                                                >
                                                    Delete
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {templates.length === 0 && (
                <div className="text-center py-20 border-2 border-dashed border-border rounded-xl">
                    <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-20" />
                    <h3 className="text-lg font-medium text-muted-foreground">No templates yet</h3>
                    <p className="text-sm text-muted-foreground">Create your first Prompt AI template to get started</p>
                    <Button onClick={handleNew} variant="outline" className="mt-4">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Template
                    </Button>
                </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingId ? "Edit Prompt AI Template" : "New Prompt AI Template"}
                        </DialogTitle>
                        <DialogDescription>
                            Configure the chat template and specialized system prompt
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Title */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">
                                    Title (English) <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Cartoon Generator"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="title_th">Title (Thai)</Label>
                                <Input
                                    id="title_th"
                                    value={formData.title_th}
                                    onChange={(e) => setFormData({ ...formData, title_th: e.target.value })}
                                    placeholder="สร้าง Prompt การ์ตูน"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="description">Description (English)</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Generate high quality prompts for cartoon style images"
                                    rows={2}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description_th">Description (Thai)</Label>
                                <Textarea
                                    id="description_th"
                                    value={formData.description_th}
                                    onChange={(e) => setFormData({ ...formData, description_th: e.target.value })}
                                    placeholder="สร้างคำสั่งสำหรับรูปภาพสไตล์การ์ตูนคุณภาพสูง"
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Hidden Prompt (CRITICAL) */}
                        <div className="space-y-2">
                            <Label htmlFor="prompt" className="flex items-center gap-2">
                                <span className="text-destructive">*</span>
                                System Prompt (Internal)
                                <Badge variant="destructive" className="text-xs">HIDDEN</Badge>
                            </Label>
                            <Textarea
                                id="prompt"
                                value={formData.prompt}
                                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                                placeholder="You are a cartoon prompt expert. When given a topic, generate a detailed prompt for veo3..."
                                rows={6}
                                className="font-mono text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                                This prompt will be sent to Gemini first as instructions.
                            </p>
                        </div>

                        {/* Credits & Sort */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="credits">Credits per message</Label>
                                <Input
                                    id="credits"
                                    type="number"
                                    value={formData.credits_required}
                                    onChange={(e) => setFormData({ ...formData, credits_required: parseInt(e.target.value) || 4 })}
                                    min={1}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="sort_order">Sort Order</Label>
                                <Input
                                    id="sort_order"
                                    type="number"
                                    value={formData.sort_order}
                                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                                    min={0}
                                />
                            </div>
                        </div>

                        {/* Visual Settings */}
                        <div className="grid grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <Label>Icon</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {ICON_OPTIONS.map((option) => {
                                        const IconComp = option.icon;
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, icon_name: option.value })}
                                                className={`p-2 rounded-lg border-2 transition-all flex items-center justify-center ${formData.icon_name === option.value
                                                        ? "border-primary bg-primary/10"
                                                        : "border-border hover:border-primary/50"
                                                    }`}
                                                title={option.label}
                                            >
                                                <IconComp className="h-5 w-5" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Color Scheme</Label>
                                <div className="flex flex-wrap gap-2">
                                    {COLOR_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, color_class: option.value })}
                                            className={`relative p-1 rounded-lg border-2 transition-all ${formData.color_class === option.value
                                                    ? "border-primary"
                                                    : "border-border hover:border-primary"
                                                }`}
                                            title={option.label}
                                        >
                                            <div className={`w-8 h-8 rounded ${option.preview}`} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Active Switch */}
                        <div className="flex items-center justify-between p-4 rounded-lg border border-border mt-4">
                            <div className="space-y-0.5">
                                <Label htmlFor="is_active">Publish Template</Label>
                                <p className="text-xs text-muted-foreground">Make this template available to users</p>
                            </div>
                            <Switch
                                id="is_active"
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Template
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PromptAiManagement;
